import {DownloadIcon} from '@heroicons/react/outline';
import type {JSX} from 'react';
import {useState} from 'react';

import {buildDownloadFileName} from '@afriex/core/utils';

import {Button} from '@/components';
import {merchantTransactionService} from '@/services/merchants/transactionService';
import type {AfriexQueryParams} from '@/types';
import {saveBlob} from '@/utils/downloadFile';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';
import {showErrorMessage} from '@/utils/showErrorMessage';

interface DownloadBusinessTransactionCSVButtonProps {
  /** The filters the list on screen was fetched with. */
  filters: AfriexQueryParams;
  /** True when the list on screen is empty, so there is nothing to export. */
  disabled: boolean;
  currentTabId?: 'swift' | 'system' | 'all';
}

const getFilePrefix = (
  currentTabId: DownloadBusinessTransactionCSVButtonProps['currentTabId'],
): string => {
  if (currentTabId === 'swift') {
    return 'swift_transactions';
  }

  if (currentTabId === 'system') {
    return 'system_transactions';
  }

  return 'business_transactions';
};

/**
 * Downloads every transaction matching the list's filters, not just the page
 * on screen: the server streams the CSV (see adminBusinessTransactionCsv.ts)
 * and this only saves it.
 */
const DownloadBusinessTransactionCSVButton = ({
  filters,
  disabled,
  currentTabId,
}: DownloadBusinessTransactionCSVButtonProps): JSX.Element => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const blob =
        await merchantTransactionService.downloadBusinessTransactionList(
          filters,
        );
      saveBlob(blob, buildDownloadFileName(getFilePrefix(currentTabId), 'csv'));
    } catch (error) {
      showErrorMessage(getErrorMessage(error, 'Failed to export transactions'));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      colorScheme="cyan"
      leftIcon={<DownloadIcon className="h-5 w-5" />}
      disabled={disabled || isDownloading}
      isLoading={isDownloading}
      loadingText="Exporting"
      onClick={() => {
        // handleDownload reports its own failures, so this only fires if that
        // reporting itself throws.
        handleDownload().catch(logRejection('Failed to export transactions'));
      }}
      testId={'download-csv-button'}
      data-cy="merchant-transactions-export"
    >
      Export as .CSV
    </Button>
  );
};

export default DownloadBusinessTransactionCSVButton;
